import { Extension } from "@tiptap/core"
import { Plugin, PluginKey, type EditorState } from "@tiptap/pm/state"
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view"
import type { MentionItem } from "@/components/prompt-editor/types"

type Placeholder = object

type PlaceholderMeta = { add: Placeholder; pos: number; label: string } | { remove: Placeholder }

const placeholderKey = new PluginKey<DecorationSet>("taskFilePastePlaceholder")

function placeholderChip(label: string): HTMLElement {
  const chip = document.createElement("span")
  chip.className = "pe-mention pe-mention--file animate-pulse"
  chip.textContent = label
  return chip
}

function isPlaceholder(placeholder: Placeholder) {
  return (spec: { placeholder?: Placeholder }) => spec.placeholder === placeholder
}

export const TaskFilePastePlaceholder = Extension.create({
  name: "taskFilePastePlaceholder",

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: placeholderKey,
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, set) => {
            const next = set.map(tr.mapping, tr.doc)
            const meta = tr.getMeta(placeholderKey) as PlaceholderMeta | undefined
            if (!meta) return next
            if ("add" in meta) {
              // side: -1 — текст, набранный во время загрузки, встаёт после чипа, а не перед ним.
              const widget = Decoration.widget(meta.pos, () => placeholderChip(meta.label), {
                placeholder: meta.add,
                side: -1,
              })
              return next.add(tr.doc, [widget])
            }
            return next.remove(next.find(undefined, undefined, isPlaceholder(meta.remove)))
          },
        },
        props: {
          decorations: (state) => placeholderKey.getState(state),
        },
      }),
    ]
  },
})

// Пока стоит чип «Загрузка…», ссылки на файл в тексте ещё нет — отправлять рано.
export function hasPendingFileUploads(state: EditorState): boolean {
  return (placeholderKey.getState(state)?.find().length ?? 0) > 0
}

function addPlaceholder(view: EditorView, label: string): Placeholder {
  const placeholder = {}
  const meta: PlaceholderMeta = { add: placeholder, pos: view.state.selection.to, label }
  view.dispatch(view.state.tr.setMeta(placeholderKey, meta))
  return placeholder
}

function replacePlaceholder(view: EditorView, placeholder: Placeholder, items: MentionItem[]) {
  if (view.isDestroyed) return
  const pos = placeholderKey.getState(view.state)?.find(undefined, undefined, isPlaceholder(placeholder))[0]?.from
  const meta: PlaceholderMeta = { remove: placeholder }
  const tr = view.state.tr.setMeta(placeholderKey, meta)
  if (pos != null && items.length > 0) {
    const { schema } = view.state
    const chips = items.flatMap((item) => [
      schema.nodes.mention.create({ category: "file", id: item.id, label: item.label }),
      schema.text(" "),
    ])
    tr.insert(pos, chips)
  }
  view.dispatch(tr)
}

export async function insertPastedFiles(
  view: EditorView,
  upload: Promise<MentionItem[]>,
  label: string,
  onUploaded: (items: MentionItem[]) => void,
) {
  const placeholder = addPlaceholder(view, label)
  const items = await upload.catch((): MentionItem[] => [])
  onUploaded(items)
  replacePlaceholder(view, placeholder, items)
}
